import React, {Fragment, useLayoutEffect, useRef, useState} from 'react'

import {Input} from './index.js'
import {InputChangeEvent, InputRef} from './Input.js'
import {FieldProps} from './types.js'
import {formSpacing} from './styles.js'

// label and info are accepted for a uniform API but not rendered: the six digit boxes have no natural place for them,
// callers put an explanation above the boxes themselves. disabled and readOnly apply to every box.
export interface VerificationCodeInputProps extends FieldProps {
    onChange?: (code: string[]) => void
}

const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({onChange, disabled, readOnly, style}) => {
    const [code, setCode] = useState(['', '', '', '', '', ''])

    const inputRefs = useRef<(InputRef | null)[]>([])

    // Index of the input to focus once the new code is rendered
    const focusIndexRef = useRef<number | null>(null)

    useLayoutEffect(() => {
        inputRefs.current[0].focus()
    }, [])

    useLayoutEffect(() => {
        if (focusIndexRef.current !== null) {
            const index = focusIndexRef.current
            focusIndexRef.current = null
            inputRefs.current[index]?.focus()
        }
    }, [code])

    const handleChange = (event: InputChangeEvent, index: number) => {
        if (disabled || readOnly) {
            return
        }

        // Pasted codes may be formatted as '123 456' or '123-456'
        let value = event.target.value.replace(/[\s-]/g, '')

        if (/^[0-9]*$/.test(value)) {
            const newCode = [...code]
            if (value.length >= 6) {
                // A full code was pasted (possibly next to the digit already in this box), fill all boxes with its last 6 digits
                value.slice(-6).split('').forEach((number, nIndex) => {
                    newCode[nIndex] = number
                })
                setCode(newCode)
                focusIndexRef.current = 5

                onChange?.(newCode)
                return
            }

            if (value.length === 2 && code[index]) {
                // Typed into a filled box, replace its digit with the new one instead of overwriting the next box
                value = value.startsWith(code[index]) ? value[1] : value[0]
            }

            if (value.length > 1) {
                value = value.substring(0, (6 - index))
                value.split('').forEach((number, nIndex) => {
                    newCode[nIndex + index] = number
                })
            } else {
                newCode[index] = value
            }

            setCode(newCode)

            if (value !== '' && index < 5) {
                if (value.length > 1) {
                    focusIndexRef.current = index + (value.length - 1)
                } else {
                    focusIndexRef.current = index + 1
                }
            }

            onChange?.(newCode)
        }
    }

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && index > 0 && code[index] === '') {
            inputRefs.current[index - 1].focus()
            inputRefs.current[index - 1].select()
        }
    }

    return (
        // The inputs shrink below 45px on narrow screens (e.g. the login on a phone)
        <div style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, maxWidth: '100%', ...style}}>
            {code.map((character, index) => (
                <Fragment key={index}>
                    <Input
                        style={{flex: '0 1 45px', minWidth: 0}}
                        boxStyle={{width: '100%'}}
                        inputStyle={{
                            fontSize: 32,
                            paddingTop: 0,
                            paddingLeft: 0,
                            paddingRight: 0,
                            paddingBottom: 0,
                            textAlign: 'center'
                        }}
                        value={character}
                        inputMode='numeric'
                        autoComplete={index === 0 ? 'one-time-code' : undefined}
                        aria-label={`Cijfer ${index + 1}`}
                        disabled={disabled}
                        readOnly={readOnly}
                        onChange={(event) => handleChange(event, index)}
                        onKeyUp={(event) => handleKeyUp(event, index)}
                        ref={(input) => {
                            inputRefs.current[index] = input
                        }}
                        onClick={() => {
                            inputRefs.current[index]?.select()
                        }}
                    />
                    {index === 2 && (
                        <span style={{fontSize: 32, height: '100%'}}>-</span>
                    )}
                </Fragment>
            ))}
        </div>
    )
}

export default VerificationCodeInput
